import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import TrustBadgesStrip from "@/components/TrustBadgesStrip";
import { getProductPhotos } from "@/data/galleryImages";
import {
  ShoppingBag,
  Palette,
  Factory,
  ChevronRight,
  ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATIC_URL = `${API}/gallery-static`;
const HERO_BG = `${STATIC_URL}/lg/hero-shades.jpg`;
const ABOUT_IMG = `${STATIC_URL}/lg/store-11.jpg`;

const industries = [
  "Boutiques",
  "Garment Manufacturers",
  "Tailors",
  "Fashion Brands",
  "Uniform Makers",
  "Export Houses",
];

export default function HomePage() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetch(`${API}/products`)
      .then((r) => r.json())
      .then(setProducts)
      .catch((e) => console.error("Products load failed:", e));
  }, []);

  // Pick the 4 hero qualities and pair each with a real photo
  const featured = products.slice(0, 4).map((p) => {
    const photos = getProductPhotos(p.id);
    return {
      ...p,
      image: photos[0]?.url || `${STATIC_URL}/lg/600-sd-sample.jpg`,
    };
  });

  return (
    <PageLayout className="!pt-0">
      <SEO
        title="Mutha Paras & Co. | Premium Lining Fabric Manufacturer Since 1974 | Bangalore"
        description="India's most trusted lining fabric house since 1974. 9 premium qualities, 2,400+ shades, 15 lakh+ meters ready stock. Order cambric, cotton & American Crepe lining on WhatsApp."
        path="/"
      />
      <main
        className="overflow-hidden"
        style={{ backgroundColor: "var(--mp-paper)", color: "var(--mp-text)" }}
        data-testid="home-page"
      >
        {/* ================= HERO ================= */}
        <section
          className="relative min-h-screen flex items-center justify-center px-6 overflow-hidden"
          data-testid="hero-section"
          style={{ backgroundColor: "var(--mp-solid)" }}
        >
          {/* Subtle fabric backdrop */}
          <div className="absolute inset-0">
            <img
              src={HERO_BG}
              alt="Mutha Paras & Co. shade catalogue backdrop"
              className="w-full h-full object-cover opacity-25"
              loading="eager"
            />
          </div>
          {/* Directional ink scrim — keeps the lockup's clear space clean */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(16,16,16,0.90) 0%, rgba(16,16,16,0.84) 45%, rgba(16,16,16,0.97) 100%)",
            }}
          />
          {/* One warm copper wash, top-left, 15% */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(135deg, rgba(184,115,51,0.12) 0%, transparent 42%)",
            }}
          />

          <div className="relative z-10 max-w-6xl mx-auto text-center pt-24 pb-16">
            {/* Featured logo */}
            <img
              src="/brand/lockup-stacked-light.svg"
              alt="Mutha Paras & Co. — Pioneers in Lining Fabric Solutions Since 1974"
              className="mx-auto w-[300px] sm:w-[420px] md:w-[520px] lg:w-[600px] h-auto animate-fade-in-up"
              data-testid="hero-logo"
              loading="eager"
            />

            {/* Accessible page heading (SEO) */}
            <h1 className="sr-only">
              Mutha Paras &amp; Co. — Premium Lining Fabric Manufacturer in Bangalore Since 1974
            </h1>

            <p
              className="mt-10 text-base md:text-lg max-w-2xl mx-auto leading-relaxed animate-fade-in-up"
              style={{
                fontFamily: "'Manrope', sans-serif",
                color: "rgba(255,255,255,0.78)",
                animationDelay: "200ms",
              }}
              data-testid="hero-tagline"
            >
              Trusted by boutiques, garment manufacturers, wholesalers and
              fashion brands across India — 9 premium qualities, 2,400+
              shades, 15 lakh+ meters of ready stock.
            </p>

            <div
              className="mt-10 flex flex-col md:flex-row gap-4 justify-center animate-fade-in-up"
              style={{ animationDelay: "300ms" }}
            >
              <Link to="/products" data-testid="hero-collections-btn">
                <button
                  className="px-10 py-5 rounded-full text-base font-medium transition duration-300 shadow-xl hover:opacity-90"
                  style={{
                    backgroundColor: "var(--mp-copper-solid)",
                    color: "var(--mp-text-strong)",
                    fontFamily: "'Manrope', sans-serif",
                  }}
                >
                  View Collections
                </button>
              </Link>
              <Link to="/order" data-testid="hero-order-btn">
                <button
                  className="px-10 py-5 rounded-full text-base font-medium transition duration-300 shadow-xl hover:bg-white hover:text-[var(--mp-text-strong)]"
                  style={{
                    border: "1px solid rgba(184,115,51,0.6)",
                    color: "#EFE2CE",
                    backgroundColor: "transparent",
                    fontFamily: "'Manrope', sans-serif",
                  }}
                >
                  Order Now
                </button>
              </Link>
            </div>

            {/* Trust strip */}
            <div
              className="mt-16 flex flex-wrap items-center justify-center gap-x-8 gap-y-6 sm:gap-x-14 animate-fade-in-up"
              style={{ animationDelay: "400ms" }}
              data-testid="hero-trust-strip"
            >
              {[
                { num: "50+", label: "Years" },
                { num: "2,400+", label: "Colours" },
                { num: "9", label: "Qualities" },
                { num: "15L+", label: "Meters Stock" },
              ].map((t) => (
                <div key={t.label} className="flex flex-col items-center">
                  <span
                    className="text-2xl md:text-3xl font-medium"
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      color: "var(--mp-copper)",
                    }}
                  >
                    {t.num}
                  </span>
                  <span
                    className="text-[10px] uppercase tracking-[0.2em] mt-1"
                    style={{
                      fontFamily: "'Manrope', sans-serif",
                      color: "rgba(255,255,255,0.6)",
                    }}
                  >
                    {t.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TRUST BADGES ================= */}
        <TrustBadgesStrip theme="light" />

        <section
          className="px-6 py-5 border-y"
          style={{ backgroundColor: "var(--mp-solid)", borderColor: "rgba(184,115,51,0.28)" }}
          data-no-reveal
          data-testid="stock-promise-band"
        >
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-center">
            <span
              className="text-xs sm:text-sm font-semibold"
              style={{ fontFamily: "'Manrope', sans-serif", color: "#F3EFE6", letterSpacing: "0.04em" }}
            >
              Ready stock on all 9 qualities
            </span>
            <span className="hidden sm:block w-px h-4" style={{ backgroundColor: "rgba(243,239,230,0.25)" }} />
            <span className="text-xs sm:text-sm" style={{ fontFamily: "'Manrope', sans-serif", color: "rgba(243,239,230,0.72)" }}>
              2,400+ shades held in Chickpet
            </span>
            <span className="hidden sm:block w-px h-4" style={{ backgroundColor: "rgba(243,239,230,0.25)" }} />
            <span className="text-xs sm:text-sm" style={{ fontFamily: "'Manrope', sans-serif", color: "rgba(243,239,230,0.72)" }}>
              Dispatched within 24 hours
            </span>
            <Link
              to="/products"
              className="text-xs sm:text-sm font-semibold copper-underline"
              style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-copper)" }}
              data-testid="stock-promise-link"
            >
              See stock status →
            </Link>
          </div>
        </section>

        {/* ================= ABOUT ================= */}
        <section
          className="py-24 lg:py-28 px-6"
          style={{ backgroundColor: "var(--mp-paper)" }}
          data-testid="about-section"
        >
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <img
                src={ABOUT_IMG}
                alt="Inside the Mutha Paras & Co. office in Chickpet, Bangalore, with shade stock on the shelves behind the desk"
                className="rounded-[36px] shadow-2xl object-cover w-full mx-auto"
                style={{ aspectRatio: "3 / 4", maxHeight: "760px", objectPosition: "28% center" }}
                loading="lazy"
                data-testid="about-image"
              />
              <div
                className="absolute -bottom-6 right-0 sm:-bottom-8 sm:-right-8 bg-white rounded-3xl shadow-2xl p-6 sm:p-8"
                data-testid="about-stat-card"
              >
                <h3
                  className="text-3xl sm:text-4xl font-medium"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
                >
                  2,400+
                </h3>
                <p
                  className="mt-1 text-xs sm:text-sm"
                  style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
                >
                  Colour Shades Available
                </p>
              </div>
            </div>

            <div>
              <div
                className="inline-block px-5 py-2 rounded-full mb-6 text-xs font-medium uppercase tracking-[0.2em]"
                style={{ backgroundColor: "var(--mp-surface-2)", color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
              >
                About Mutha Paras &amp; Co.
              </div>

              <h2
                className="text-4xl md:text-5xl font-medium leading-tight tracking-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
              >
                Bangalore’s Trusted Textile Legacy
              </h2>

              <p
                className="mt-6 text-base lg:text-lg leading-relaxed"
                style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
              >
                Mutha Paras &amp; Co. has been manufacturing premium quality lining
                fabrics since 1974. We specialise in cambric lining, polycotton
                lining, synthetic lining and shrinkless fabrics for boutiques,
                wholesalers, garment manufacturers and fashion exporters across India.
              </p>

              <div className="grid sm:grid-cols-2 gap-5 mt-10">
                <div
                  className="bg-white p-7 rounded-3xl shadow-lg border border-[var(--mp-line)]"
                  data-testid="about-feature-manufacturing"
                >
                  <Factory className="mb-4" size={32} style={{ color: "var(--mp-text-strong)" }} />
                  <h3
                    className="text-xl font-medium"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
                  >
                    Bulk Manufacturing
                  </h3>
                  <p
                    className="mt-2 text-sm leading-relaxed"
                    style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
                  >
                    Consistent quality with 15 lakh+ meters of ready stock and up to 8,000 m of every running shade — available at any given point of time.
                  </p>
                </div>
                <div
                  className="bg-white p-7 rounded-3xl shadow-lg border border-[var(--mp-line)]"
                  data-testid="about-feature-shade"
                >
                  <Palette className="mb-4" size={32} style={{ color: "var(--mp-copper)" }} />
                  <h3
                    className="text-xl font-medium"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
                  >
                    Shade Matching
                  </h3>
                  <p
                    className="mt-2 text-sm leading-relaxed"
                    style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
                  >
                    2,400+ ready shades plus custom dyeing for any colour you need.
                  </p>
                </div>
              </div>

              <Link
                to="/heritage"
                className="inline-flex items-center gap-2 mt-8 text-sm font-medium hover:gap-3 transition-all"
                style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text-strong)" }}
                data-testid="about-heritage-link"
              >
                Read our 3-generation story <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>

        {/* ================= PRODUCTS ================= */}
        <section
          className="py-24 lg:py-28 px-6"
          style={{ backgroundColor: "var(--mp-surface-2)" }}
          data-testid="featured-products"
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto">
              <div
                className="inline-block px-5 py-2 rounded-full text-xs font-medium uppercase tracking-[0.2em]"
                style={{ backgroundColor: "var(--mp-surface-2)", color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
              >
                Our Collections
              </div>
              <h2
                className="text-4xl md:text-5xl font-medium mt-6 tracking-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
              >
                Explore Our Lining Qualities
              </h2>
              <p
                className="mt-5 text-base lg:text-lg leading-relaxed"
                style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
              >
                Premium lining fabrics engineered for comfort, durability,
                shrink resistance and superior garment finishing.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mt-16">
              {featured.map((p, idx) => (
                <Link
                  key={p.id}
                  to={`/product/${p.id}`}
                  className="group block bg-[var(--mp-card)] rounded-[28px] overflow-hidden shadow-lg hover:shadow-2xl hover:border-[var(--mp-copper)] transition duration-500 border border-[var(--mp-line)] animate-fade-in-up"
                  style={{ animationDelay: `${idx * 80}ms`, textDecoration: "none" }}
                  data-testid={`featured-product-${p.id}`}
                >
                  <div className="relative h-72 overflow-hidden bg-white">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6 lg:p-7">
                    <h3
                      className="text-xl lg:text-2xl font-medium tracking-tight"
                      style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
                    >
                      {p.name}
                    </h3>
                    <p
                      className="text-[10px] uppercase tracking-[0.15em] font-semibold mt-1"
                      style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
                    >
                      {p.colors_available} colours · {p.width}
                    </p>
                    <p
                      className="mt-4 text-sm leading-relaxed line-clamp-3"
                      style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
                    >
                      {(p.description || "").slice(0, 120)}…
                    </p>
                    <span
                      className="mt-6 flex items-center gap-2 font-semibold text-sm group-hover:gap-3 transition-all"
                      style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text-strong)" }}
                    >
                      View Shades <ChevronRight size={18} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-14">
              <Link to="/products" data-testid="view-all-products-link">
                <button
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm font-medium border-2 transition hover:bg-[var(--mp-solid)] hover:text-white"
                  style={{ borderColor: "var(--mp-solid)", color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}
                >
                  View All 9 Qualities <ArrowRight size={16} />
                </button>
              </Link>
            </div>
          </div>
        </section>

        {/* ================= SAMPLE / ORDER CTA ================= */}
        <section
          className="py-24 lg:py-28 px-6"
          style={{ backgroundColor: "var(--mp-paper)" }}
          data-testid="order-cta-section"
        >
          <div
            className="max-w-6xl mx-auto rounded-[40px] p-12 md:p-20 text-center shadow-2xl border border-[var(--mp-line)]"
            style={{
              background: "linear-gradient(120deg, var(--mp-surface) 0%, var(--mp-surface-2) 100%)",
            }}
          >
            <ShoppingBag size={56} className="mx-auto mb-6" style={{ color: "var(--mp-text-strong)" }} />
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-medium leading-tight tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            >
              Order by Shade Number
            </h2>
            <p
              className="mt-6 text-base md:text-lg max-w-3xl mx-auto leading-relaxed"
              style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
            >
              Browse all 446 American Crepe shades and the full 336-shade Lining
              charts. Pick by quality and number, set meters, and send the
              complete order straight to our WhatsApp.
            </p>
            <div className="mt-10 flex flex-col md:flex-row justify-center gap-4">
              <Link to="/catalogue/600-lining" data-testid="cta-lining-btn">
                <button
                  className="px-10 py-5 rounded-full text-base font-medium transition duration-300 shadow-xl text-white hover:opacity-90"
                  style={{ backgroundColor: "var(--mp-copper-solid)", fontFamily: "'Manrope', sans-serif" }}
                >
                  Lining Catalogue
                </button>
              </Link>
              <Link to="/catalogue/american-crepe" data-testid="cta-crepe-btn">
                <button
                  className="px-10 py-5 rounded-full text-base font-medium transition duration-300 shadow-xl text-white hover:opacity-90"
                  style={{ backgroundColor: "var(--mp-solid)", fontFamily: "'Manrope', sans-serif" }}
                >
                  American Crepe Catalogue
                </button>
              </Link>
              <Link to="/gallery" data-testid="cta-shade-btn">
                <button
                  className="bg-white px-10 py-5 rounded-full text-base font-medium transition duration-300 shadow-xl hover:shadow-md"
                  style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
                >
                  View Shade Charts
                </button>
              </Link>
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES ================= */}
        <section
          className="py-24 lg:py-28 px-6"
          style={{ backgroundColor: "var(--mp-surface)" }}
          data-testid="industries-section"
        >
          <div className="max-w-7xl mx-auto text-center">
            <div
              className="inline-block px-5 py-2 rounded-full text-xs font-medium uppercase tracking-[0.2em]"
              style={{ backgroundColor: "var(--mp-surface-2)", color: "var(--mp-copper-deep)", fontFamily: "'Manrope', sans-serif" }}
            >
              Industries We Serve
            </div>
            <h2
              className="text-4xl md:text-5xl font-medium mt-6 tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            >
              Trusted Across The Textile Industry
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-16">
              {industries.map((industry, index) => (
                <div
                  key={industry}
                  className="bg-[var(--mp-card)] rounded-2xl p-6 lg:p-8 shadow-sm hover:shadow-xl hover:border-[var(--mp-copper)] transition duration-300 border border-[var(--mp-line)] animate-fade-in-up"
                  style={{ animationDelay: `${index * 60}ms` }}
                  data-testid={`industry-${industry.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <h3
                    className="text-base lg:text-lg font-medium"
                    style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
                  >
                    {industry}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= FINAL CTA ================= */}
        <section
          className="py-24 lg:py-28 px-6"
          style={{ backgroundColor: "var(--mp-paper)" }}
          data-testid="final-cta-section"
        >
          <div className="max-w-5xl mx-auto text-center">
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-medium leading-tight tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            >
              Looking For Premium
              <br />
              Lining Fabrics?
            </h2>
            <p
              className="mt-6 text-lg leading-relaxed"
              style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
            >
              Contact our team for shade matching, bulk orders and sample
              requests across India and worldwide shipping.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/sample-kit" data-testid="final-sample-btn">
                <button
                  className="px-12 py-5 rounded-full text-base font-medium transition duration-300 shadow-xl text-white hover:opacity-90"
                  style={{ backgroundColor: "var(--mp-copper-solid)", fontFamily: "'Manrope', sans-serif" }}
                >
                  Order Sample Kit · ₹299
                </button>
              </Link>
              <Link to="/contact" data-testid="final-contact-btn">
                <button
                  className="px-12 py-5 rounded-full text-base font-medium transition duration-300 shadow-xl text-white hover:opacity-90"
                  style={{ backgroundColor: "var(--mp-solid)", fontFamily: "'Manrope', sans-serif" }}
                >
                  Contact Us
                </button>
              </Link>
              <a
                href="https://wa.me/917624853840"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="final-whatsapp-btn"
              >
                <button
                  className="px-12 py-5 rounded-full text-base font-medium transition duration-300 shadow-xl text-white hover:opacity-90 whatsapp-pulse"
                  style={{ backgroundColor: "#25D366", fontFamily: "'Manrope', sans-serif" }}
                >
                  WhatsApp
                </button>
              </a>
            </div>
          </div>
        </section>
      </main>
    </PageLayout>
  );
}
